import { Box, Button, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { removeSelectedOrgFromLocalStorage, UserSession } from "../../auth/AuthService";
import DotLegalModal from "../../common/modal/DotLegalModal";
import SingleSelect from "../../common/select/SingleSelect";
import { verifySelectedOrganization } from "../../core/organizations/OrganizationService";
import { getAllowedOrganizations } from "../../core/users/UserService";

export type SelectOrganizationProps = {
    userSession: UserSession;
};

function SelectOrganization(props: SelectOrganizationProps) {
    const history = useHistory();
    const location = useLocation();

    const { data: allowedOrganizations, isLoading: isLoadingOrgs } = useQuery("allowed-organizations", getAllowedOrganizations, {
        enabled: !!props.userSession.verifiedUser,
    });

    const chooseOrgMutation = useMutation(verifySelectedOrganization);

    const selectedOrg = props.userSession.selectedOrg;
    const currentOrgId = selectedOrg ? selectedOrg.id.toString() : null;
    const [pickedOrgId, setPickedOrgId] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(true);
    const displayedOrgId = pickedOrgId ?? currentOrgId;

    // Re-open the modal when the user navigates to /select-organisation again (e.g., clicking the menu while already on the route).
    // Same-URL pushes give a new location.key but don't remount the component, so we sync isOpen here.
    useEffect(() => {
        setIsOpen(true);
    }, [location.key]);

    const chooseOrganization = async () => {
        if (!pickedOrgId || pickedOrgId === currentOrgId) {
            setIsOpen(false);
            history.push("/dashboard");
            return;
        }
        // HttpInterceptor overwrites the organizationId header with localStorage's value on every request,
        // so we must clear it first — otherwise verifySelectedOrganization hits the backend with the old org.
        removeSelectedOrgFromLocalStorage();
        await chooseOrgMutation.mutateAsync(pickedOrgId, {
            onSuccess: (newSelectedOrg) => {
                if (!newSelectedOrg) {
                    setPickedOrgId(null);
                    props.userSession.onSelectedOrganizationChanged(null);
                    return;
                }
                props.userSession.onSelectedOrganizationChanged(newSelectedOrg);
                const redirectTo = new URLSearchParams(window.location.search).get("redirect");
                history.push(redirectTo ?? "/dashboard");
            },
            onError: () => setPickedOrgId(null),
        });
    };

    const options = (allowedOrganizations ?? []).map((org) => ({ id: org.id.toString(), name: org.name }));
    const isVerifying = chooseOrgMutation.isLoading;
    const canOpen = !!displayedOrgId && !isVerifying;

    const closeModal = () => {
        if (isVerifying) return;
        setPickedOrgId(null);
        setIsOpen(false);
    };

    return (
        <DotLegalModal
            open={isOpen}
            onClose={closeModal}
            maxWidth="sm"
            title="Select Organisation"
            content={
                <Box
                    sx={{
                        margin: 1,
                        "& .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input": {
                            width: "100%",
                        },
                    }}
                >
                    <SingleSelect
                        label="Organisation"
                        placeholder=""
                        noOptionsLabel="No organisations"
                        options={options}
                        selectedItem={displayedOrgId}
                        onChange={(orgId) => setPickedOrgId(orgId)}
                        disableClearable
                        isLoading={isLoadingOrgs}
                        noMargin
                        disabled={isVerifying}
                    />
                </Box>
            }
            action={
                <Button color="primary" variant="outlined" disabled={!canOpen} onClick={chooseOrganization}>
                    {isVerifying ? <CircularProgress size={24} sx={{ color: "#ffffff" }} /> : "Open"}
                </Button>
            }
        />
    );
}

export default SelectOrganization;
